import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import HCaptcha from "@hcaptcha/react-hcaptcha";

const SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY || "";

/**
 * <Captcha /> — thin wrapper around @hcaptcha/react-hcaptcha.
 *
 * Two modes:
 *   invisible=false (default) → visible "I am human" checkbox. Fine on plain
 *                               pages / forms.
 *   invisible=true            → no visible widget. Parent calls
 *                               `ref.execute()` to trigger verification. Ideal
 *                               inside modal dialogs where the checkbox
 *                               callback path has been unreliable due to
 *                               focus-trap interactions.
 *
 * Callbacks:
 *   onToken(token) → fired when the user solves the challenge
 *   onToken(null)  → token expired / errored out
 *
 * Imperative handle (via ref):
 *   .reset()        → clear the widget so a fresh challenge is required
 *   .getResponse()  → synchronously read the current verified token
 *   .execute()      → invisible mode only: launch the challenge programmatically.
 *                     Returns a Promise that resolves with the token (or rejects).
 */
const Captcha = forwardRef(function Captcha(
  { onToken, theme = "light", testId = "hcaptcha-widget", invisible = false },
  ref,
) {
  const innerRef = useRef(null);
  // Pending execute() promise — resolved from onVerify / onError / onExpire.
  const pendingRef = useRef(null);
  const [errored, setErrored] = useState(false);

  useImperativeHandle(
    ref,
    () => ({
      reset: () => {
        try { innerRef.current?.resetCaptcha?.(); } catch (_e) { /* noop */ }
      },
      getResponse: () => {
        try {
          const t = innerRef.current?.getResponse?.();
          return t || null;
        } catch (_e) {
          return null;
        }
      },
      execute: () => new Promise((resolve, reject) => {
        // If we already have a valid response, reuse it — hCaptcha tokens are
        // one-shot but the widget hangs on to the most recent one until reset.
        try {
          const existing = innerRef.current?.getResponse?.();
          if (existing) return resolve(existing);
        } catch (_e) { /* fall through */ }

        if (!innerRef.current?.execute) {
          return reject(new Error("Captcha widget not ready — please try again."));
        }
        pendingRef.current = { resolve, reject };
        try {
          innerRef.current.execute();
        } catch (e) {
          pendingRef.current = null;
          reject(e);
        }
      }),
    }),
    [],
  );

  const handleVerify = (token) => {
    onToken?.(token);
    if (pendingRef.current) {
      pendingRef.current.resolve(token);
      pendingRef.current = null;
    }
  };
  const handleError = (err) => {
    onToken?.(null);
    setErrored(true);
    if (pendingRef.current) {
      pendingRef.current.reject(err || new Error("Captcha challenge failed."));
      pendingRef.current = null;
    }
  };
  const handleExpire = () => {
    onToken?.(null);
    // Do NOT reject an in-flight execute here — the widget will auto-reset
    // and the next execute() call will get a fresh token.
  };

  if (!SITE_KEY) {
    return (
      <div className="text-xs text-[#7A5A22] bg-[#FBF7F1] border border-dashed border-[#E7DDCF] rounded-md px-3 py-2" data-testid={`${testId}-misconfigured`}>
        Captcha unavailable (site key missing).
      </div>
    );
  }

  // In invisible mode we render the widget in a zero-height wrapper so it
  // remains in the DOM (needed for `execute()` to work) but doesn't reserve
  // any visible space. A short hint line is rendered instead so users know
  // an anti-bot check will run when they submit.
  if (invisible) {
    return (
      <div data-testid={testId}>
        {errored ? (
          <div className="text-xs text-[#B45309] bg-[#FEF3E0] border border-[#F4D48A] rounded-md px-2.5 py-1.5" data-testid={`${testId}-error`}>
            Anti-bot check failed. Please try again.
          </div>
        ) : (
          <div className="text-[11px] text-[#7A5A22] flex items-center gap-1">
            <span>Protected by hCaptcha — a quick check may appear on submit.</span>
          </div>
        )}
        <div style={{ height: 0, overflow: "hidden" }}>
          <HCaptcha
            ref={innerRef}
            sitekey={SITE_KEY}
            size="invisible"
            theme={theme}
            onVerify={handleVerify}
            onExpire={handleExpire}
            onError={handleError}
          />
        </div>
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      <HCaptcha
        ref={innerRef}
        sitekey={SITE_KEY}
        theme={theme}
        onVerify={handleVerify}
        onExpire={handleExpire}
        onError={handleError}
      />
    </div>
  );
});

export default Captcha;
